import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CancelSubscriptionPayload,
  DeactivateSubscriptionPayload,
  DeactivationAudience,
  RecordRenewalInvoicePayload,
  RecordRenewalPaymentPayload,
  RenewalClientDetailsPayload,
  RenewalDashboardParams,
  RenewalListParams,
  RenewalPricingPatchPayload,
  renewalsService,
  SendInternalNoticePayload,
  SendRenewalInvoicePayload,
} from '@/services/renewals.service';
import { QUERY_KEYS } from '@/utils/constants';

export const useRenewalsList = (params: RenewalListParams) =>
  useQuery({
    queryKey: [...QUERY_KEYS.RENEWALS.LIST, params],
    queryFn: () => renewalsService.list(params),
    placeholderData: keepPreviousData,
  });

export const useRenewalMetrics = () =>
  useQuery({
    queryKey: QUERY_KEYS.RENEWALS.METRICS,
    queryFn: () => renewalsService.metrics(),
  });

export const useRenewalDashboard = (params: RenewalDashboardParams) =>
  useQuery({
    queryKey: [...QUERY_KEYS.RENEWALS.DASHBOARD, params],
    queryFn: () => renewalsService.dashboard(params),
    placeholderData: keepPreviousData,
  });

export const useRenewalCalendar = (year: number, month: number) =>
  useQuery({
    queryKey: [...QUERY_KEYS.RENEWALS.CALENDAR, year, month],
    queryFn: () => renewalsService.calendar(year, month),
    placeholderData: keepPreviousData,
  });

export const useRenewal = (id: number, enabled = true) =>
  useQuery({
    queryKey: QUERY_KEYS.RENEWALS.BY_ID(id),
    queryFn: () => renewalsService.getById(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });

// Invalidates every renewals query so the queue, KPI cards, calendar and the
// open detail all reconcile after a workflow transition. onSettled (success AND
// error) mirrors useBilling: a slow send can complete server-side after the
// client aborts, so refetching on error self-heals the UI.
export const useInvalidateRenewals = () => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RENEWALS.ROOT });
  };
};

export const useZeroAumHolds = () =>
  useQuery({
    queryKey: QUERY_KEYS.RENEWALS.ZERO_AUM_HOLDS,
    queryFn: () => renewalsService.zeroAumHolds(),
  });

// Live AUM fetch: the returned detail replaces the cached one immediately, and
// the queue/Zero AUM lists refetch because the amount or hold may have moved.
export const useRefreshRenewalAum = (id: number) => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: () => renewalsService.refreshAum(id),
    onSuccess: (result) => {
      queryClient.setQueryData(QUERY_KEYS.RENEWALS.BY_ID(id), result.detail);
    },
    onSettled: invalidate,
  });
};

export const useUpdateRenewalClientDetails = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: RenewalClientDetailsPayload) =>
      renewalsService.updateClientDetails(id, payload),
    onSettled: invalidate,
  });
};

export const useUpdateRenewalPricing = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: RenewalPricingPatchPayload) => renewalsService.updatePricing(id, payload),
    onSettled: invalidate,
  });
};

export const useUpdateRenewalDueDate = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (input: { dueDate: string; note?: string }) =>
      renewalsService.updateDueDate(id, input.dueDate, input.note),
    onSettled: invalidate,
  });
};

// ─── Deactivation / reactivation (ADR-054) ───────────────────────────────────
export const useStartDeactivation = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: DeactivateSubscriptionPayload) =>
      renewalsService.startDeactivation(id, payload),
    onSettled: invalidate,
  });
};

export const useSendDeactivationEmail = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (input: { audience: DeactivationAudience; payload: SendInternalNoticePayload }) =>
      renewalsService.sendDeactivationEmail(id, input.audience, input.payload),
    onSettled: invalidate,
  });
};

export const useAbortDeactivation = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: () => renewalsService.abortDeactivation(id),
    onSettled: invalidate,
  });
};

export const useReactivateRenewal = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: () => renewalsService.reactivate(id),
    onSettled: invalidate,
  });
};

export const useSendRenewalTermination = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: () => renewalsService.sendTermination(id),
    onSettled: invalidate,
  });
};

export const useVerifyRenewal = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (notes?: string) => renewalsService.verify(id, notes),
    onSettled: invalidate,
  });
};

export const useRecordRenewalPayment = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: RecordRenewalPaymentPayload) =>
      renewalsService.recordPayment(id, payload),
    onSettled: invalidate,
  });
};

export const useRecordRenewalInvoice = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: RecordRenewalInvoicePayload) =>
      renewalsService.recordInvoice(id, payload),
    onSettled: invalidate,
  });
};

export const useNotifyRenewalService = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: SendInternalNoticePayload) => renewalsService.notifyService(id, payload),
    onSettled: invalidate,
  });
};

export const useSendRenewalInvoice = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: SendRenewalInvoicePayload) => renewalsService.sendInvoice(id, payload),
    onSettled: invalidate,
  });
};

export const useCompleteRenewal = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: () => renewalsService.complete(id),
    onSettled: invalidate,
  });
};

export const useCancelSubscription = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: (payload: CancelSubscriptionPayload) =>
      renewalsService.cancelSubscription(id, payload),
    onSettled: invalidate,
  });
};

// Skip this renewal cycle (Non-ARN clients only, ADKJ-117).
export const useSkipRenewal = (id: number) => {
  const invalidate = useInvalidateRenewals();
  return useMutation({
    mutationFn: () => renewalsService.skipRenewal(id),
    onSettled: invalidate,
  });
};
